<script setup lang="ts">
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import type { PostColor } from '@@/types'
import { computed } from 'vue'

const props = defineProps<{
  iconName: string
  title: string
  ariaLabel: string
  postColor: PostColor
  colorScheme: 'light' | 'dark'
  size: '24px' | '32px' | '64px'
  tag?: string
  /** Native HTML slot attribute to be set for the OzIcon. */
  iconSlotName?: string
  testId?: string
}>()

defineEmits<{
  (name: 'click', event: MouseEvent): void
}>()

const isLightColorScheme = computed<boolean>(() => props.colorScheme === 'light')

const buttonColorClass = computed<string>(() => {
  switch (props.postColor) {
    case 'red':
      return 'bg-scarlet-600'
    case 'orange':
      return isLightColorScheme.value ? 'bg-canary-600' : 'bg-tangerine-600'
    case 'green':
      return 'bg-park-600'
    case 'blue':
      return 'bg-oceanic-600'
    case 'purple':
      return 'bg-grape-600'
    default:
      return 'bg-dark-ui-200'
  }
})

const buttonHoverColorClass = computed<string>(() => {
  switch (props.postColor) {
    case 'red':
      return 'hover-hover:hover:bg-scarlet-500'
    case 'orange':
      return isLightColorScheme.value ? 'hover-hover:hover:bg-canary-500' : 'hover-hover:hover:bg-tangerine-500'
    case 'green':
      return 'hover-hover:hover:bg-park-500'
    case 'blue':
      return 'hover-hover:hover:bg-oceanic-500'
    case 'purple':
      return 'hover-hover:hover:bg-grape-500'
    default:
      return 'hover-hover:hover:bg-dark-ui-300'
  }
})

const buttonIconColorClass = computed<string>(() => {
  switch (props.postColor) {
    case 'red':
      return 'text-scarlet-200'
    case 'orange':
      return isLightColorScheme.value ? 'text-canary-200' : 'text-tangerine-200'
    case 'green':
      return 'text-park-200'
    case 'blue':
      return 'text-oceanic-200'
    case 'purple':
      return 'text-grape-200'
    default:
      return 'text-light-text-200'
  }
})

const iconSize = computed<number>(() => {
  switch (props.size) {
    case '24px':
      return 12
    case '32px':
      return 16
    case '64px':
    default:
      return 32
  }
})
</script>

<template>
  <div :class="['absolute', 'inset-0', 'flex', 'items-center', 'justify-center', 'p-0.875', 'rounded-full']">
    <component
      :is="tag || OzBaseButton"
      :title="title"
      :aria-label="ariaLabel"
      :class="[
        // Shape
        {
          'w-6 h-6': size === '24px',
          'w-8 h-8': size === '32px',
          'w-16 h-16': size === '64px',
        },
        'rounded-full',
        'pointer-events-auto',

        // Centralize button content
        'flex',
        'items-center',
        'justify-center',

        // Colors
        buttonColorClass,
        buttonHoverColorClass,
        buttonIconColorClass,
      ]"
      :data-testid="testId"
      @click="$emit('click', $event)"
      @pointerdown.stop
    >
      <!-- This is a native slot attribute, not Vue's slot attribute -->
      <!-- so we can disable this ESLint rule. -->
      <!-- eslint-disable-next-line vue/no-deprecated-slot-attribute -->
      <OzIcon :slot="iconSlotName" :name="iconName" :size="iconSize" />
    </component>
  </div>
</template>
